import { call, put, takeEvery, takeLatest, delay, fork, cancel, take } from 'redux-saga/effects';
import {
    FETCH_MARKET_DATA_REQUEST,
    START_MARKET_DATA_POLLING,
    STOP_MARKET_DATA_POLLING,
    fetchMarketDataSuccess,
    fetchMarketDataFailure,
    fetchMarketDataRequest
} from '../reducers/addorderReducer';
import { getMarketData } from '../../services/allApiService';
// Used to skip this REST poll while the Socket.IO bridge is delivering
// ticks (marketSocketSaga keeps state.addorder.marketData fresh).
import { isMarketSocketConnected } from '../../services/socket/marketSocket';

// Your existing fetch market data saga
function* handleFetchMarketData(action) {
    try {
        const { tab, symbolTokensArray } = action.payload;
        const tokens = symbolTokensArray.map(item => item.token);
        // remap tab value before using it
        let mappedTab = tab;
        if (tab === "EQUITY" || tab === "EQUITY(INDEX)") {
            mappedTab = "NFO";
        }
        if (tokens.length === 0) return;

        // ── ONE-TIME snapshot for last prices (works even when the market is
        // closed). The polling loop is disabled while the socket is connected,
        // so this fires once on load; the socket then keeps prices live.
        const mode = 'FULL';
        const exchangeTokens = { [mappedTab]: tokens };
        const response = yield call(getMarketData, mode, exchangeTokens);

        if (response && response.data && response.data.fetched) {
            const data = response.data.fetched.sort((a, b) =>
                a.tradingSymbol.localeCompare(b.tradingSymbol)
            );

            const sortedData = data.map(item => {
                const matched = symbolTokensArray.find(
                    entry => String(entry.token) === String(item.symbolToken)
                );
                return {
                    ...item,
                    script_id: matched?.script_id || null,
                    exchange_id: matched?.exchange_id || null,
                    watchlist_id: matched?.id || null,
                };
            });

            yield put(fetchMarketDataSuccess(sortedData));

            // NOTE: do NOT write the shared "marketData" localStorage key here —
            // the AddOrder screen only has a single script, and writing it would
            // clobber the Market tab's cached gainers/losers list (which is why
            // returning from AddOrder used to show just one script).

        } else {
            yield put(fetchMarketDataFailure('Invalid response format'));
        }
    } catch (error) {
        yield put(fetchMarketDataFailure(error.message));
    }
}

// Polling saga for continuous market data updates
function* handleMarketDataPolling(action) {
    try {
        const { tab, symbolTokensArray, interval } = action.payload;

        // Do one immediate fetch so the page is populated right away even
        // when the socket will take over a moment later (it gives us
        // tradingSymbol/lots/script_id etc. that ticks don't carry).
        yield put(fetchMarketDataRequest(tab, symbolTokensArray));

        while (true) {
            yield delay(interval);

            // ── Skip REST poll while socket is delivering ticks. ──
            // marketSocketSaga is already pushing tick-batched updates
            // into state.addorder.marketData via FETCH_MARKET_DATA_SUCCESS.
            // We resume polling automatically the moment the socket drops.
            if (isMarketSocketConnected()) {
                continue;
            }

            yield put(fetchMarketDataRequest(tab, symbolTokensArray));
        }
    } catch (error) {
        console.error('Market data polling error:', error);
        yield put(fetchMarketDataFailure(error.message));
    }
}

// Saga to manage polling lifecycle
function* watchMarketDataPolling() {
    while (true) {
        const action = yield take(START_MARKET_DATA_POLLING);

        // Start polling task
        const pollingTask = yield fork(handleMarketDataPolling, action);

        // Wait for stop action
        yield take(STOP_MARKET_DATA_POLLING);

        // Cancel polling task
        yield cancel(pollingTask);
    }
}

// Fetch single symbol market data (for AddOrder component)
function* handleFetchSingleMarketData(action) {
    try {
        const { tab, symbolToken, script_id, exchange_id } = action.payload;

        const symbolTokensArray = [{
            token: symbolToken,
            script_id,
            exchange_id,
            id: null
        }];

        yield call(handleFetchMarketData, {
            payload: { tab, symbolTokensArray }
        });
    } catch (error) {
        yield put(fetchMarketDataFailure(error.message));
    }
}

// Export individual sagas for integration
export function* addOrderSaga() {
    yield takeEvery(FETCH_MARKET_DATA_REQUEST, handleFetchMarketData);
    yield fork(watchMarketDataPolling);
    yield takeLatest('FETCH_SINGLE_MARKET_DATA_REQUEST', handleFetchSingleMarketData);
}